import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, Input } from '@clari/ui'
import { FxCalculator } from '@/components/banking/FxCalculator'
import { RealRateDisplay } from '@/components/banking/RealRateDisplay'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { bankingApi } from '@/lib/banking-api'

export function Fx() {
  const { t, i18n } = useTranslation('banking')
  const accountsQ = useQuery({ queryKey: ['accounts'], queryFn: () => bankingApi.listAccounts() })
  const accounts = accountsQ.data?.items ?? []

  const [sourceId, setSourceId] = useState('')
  const [destId, setDestId] = useState('')
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [loading, setLoading] = useState(false)

  const source = accounts.find((a) => a.id === sourceId)
  const dest = accounts.find((a) => a.id === destId)
  const sameCurrency = !!source && !!dest && source.currency === dest.currency

  const quoteQ = useQuery({
    queryKey: ['fx-quote', source?.currency, dest?.currency],
    queryFn: () => bankingApi.fxQuote(source?.currency ?? '', dest?.currency ?? ''),
    enabled: !!source && !!dest && !sameCurrency,
  })

  async function convert() {
    if (!source || !dest) return
    setError(null)
    setLoading(true)
    try {
      const cents = Math.round(Number.parseFloat(amount.replace(',', '.')) * 100)
      await bankingApi.convert({
        source_account_id: source.id,
        destination_account_id: dest.id,
        source_amount_cents: cents,
      })
      setDone(true)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('fx.title')}</h1>

      <Card>
        <h2 className="mb-3 text-lg font-medium">{t('fx.calculator')}</h2>
        <FxCalculator />
      </Card>

      {accounts.length >= 2 ? (
        <Card>
          <h2 className="mb-3 text-lg font-medium">{t('fx.convert')}</h2>
          {done ? (
            <p className="text-success">{t('fx.success')}</p>
          ) : (
            <div className="flex flex-col gap-3">
              <label className="text-sm">
                <span className="text-text-muted">{t('fx.source_account')}</span>
                <select className="clari-input mt-1 w-full" value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
                  <option value="">—</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {`${a.iban} (${a.currency})`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="text-text-muted">{t('fx.destination_account')}</span>
                <select className="clari-input mt-1 w-full" value={destId} onChange={(e) => setDestId(e.target.value)}>
                  <option value="">—</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {`${a.iban} (${a.currency})`}
                    </option>
                  ))}
                </select>
              </label>
              <Input
                label={t('fx.you_convert')}
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              {quoteQ.data ? <RealRateDisplay quote={quoteQ.data} locale={i18n.language} /> : null}
              {error ? <ErrorBanner message={error} /> : null}
              <Button onClick={convert} disabled={loading || !quoteQ.data || !amount} withSignatureDot>
                {t('fx.convert')}
              </Button>
            </div>
          )}
        </Card>
      ) : null}
    </div>
  )
}
